import { CentralConfig } from './../config/CentralConfig';
import { Constants } from '../common/Constants';
import { DateTimeUtil } from '../common/DateTimeUtil';
import * as Enums from '../common/Enumerations';
import { PushType } from '../common/EnumTypes';
import * as InternalTypes from '../common/InternalTypes';
import { Logger } from '../common/Logger';
import { LoggerTexts } from '../common/LoggerTexts';
import { Util } from '../common/Util';
import { PushConfig } from '../config/push/PushConfig';
import { AppPushState } from '../data/AppPushState';
import { ContextDataController } from '../data/ContextDataController';
import { InstantPushConfigState } from '../data/InstantPushConfigState';
import { PushContextData } from '../data/PushContextData';
import { UserState } from '../data/UserState';
import { StorageService } from '../storage/StorageService';
import { DateCalculator } from '../survey/DateCalculator';
import SurveyFactory from '../survey/SurveyFactory';

export class PushHandler {
	private readonly _logger: Logger = new Logger(Constants.FILE_PUSH_HANDLER);
	private readonly _centralConfig: CentralConfig;
	private readonly _storage: StorageService;
	private readonly _contextDataController: ContextDataController;
	private _stateConfig?: InternalTypes.StateConfig;

	constructor(storage: StorageService, centralConfig: CentralConfig, contextDataController: ContextDataController) {
		this._storage = storage;
		this._centralConfig = centralConfig;
		this._contextDataController = contextDataController;
	}

	public get pushConfig(): PushConfig | undefined {
		return this._centralConfig.pushConfig;
	}

	public set stateConfig(stateConfig: InternalTypes.StateConfig | undefined) {
		this._stateConfig = stateConfig;
	}

	public get stateConfig(): InternalTypes.StateConfig | undefined {
		return this._stateConfig;
	}

	public getPushType(): Enums.PUSH_SRC_TYPE {
		if (this.stateConfig) {
			if (this.stateConfig.state instanceof AppPushState) {
				return Enums.PUSH_SRC_TYPE.appPush;
			} else if (this.stateConfig.state instanceof InstantPushConfigState) {
				return Enums.PUSH_SRC_TYPE.instantPush;
			}
		}
		return Enums.PUSH_SRC_TYPE.timed;
	}

	public getPushTypeForConsumers(): PushType {
		if (this.getPushType() === Enums.PUSH_SRC_TYPE.appPush) {
			return PushType.appPush;
		}
		if (this.getPushType() === Enums.PUSH_SRC_TYPE.instantPush) {
			return PushType.instantPush;
		}
		return PushType.timedPush;
	}

	public snoozePush() {
		const userState = this._getUserState();
		if (userState && this.pushConfig) {
			if (this.getPushType() === Enums.PUSH_SRC_TYPE.instantPush) {
				this._updateSnoozeValues(1, this.pushConfig);
			} else {
				const snoozeCount = userState.snoozeCount;
				if (snoozeCount >= Constants.LIMITS.MAX_SNOOZE_COUNT) {
					this._skipOnSnooze();
				} else {
					this._updateSnoozeValues(snoozeCount + 1, this.pushConfig);
				}
			}
		}
	}

	public updateLastPushTimestamp() {
		const userState = this._getUserState();
		if (userState) {
			userState.updateLastPushToNow();
			userState.resetSnoozeCount();
			this._saveUserState(userState);
		}
	}

	public updatePushState() {
		if (this.stateConfig) {
			const state = this.stateConfig.state;
			const userState = this._getUserState();
			let latestState: AppPushState | InstantPushConfigState | undefined;
			if (userState) {
				if (this.getPushType() === Enums.PUSH_SRC_TYPE.instantPush) {
					latestState = userState.getInstantPushConfigState(state.areaId, state.triggerName);
				} else {
					userState.resetSnoozeCount();
					latestState = userState.getAppPushState(state.areaId, state.triggerName);
				}
				if (latestState) {
					latestState.increaseExecutedCount();
					this._saveUserState(userState);
				} else {
					this._logger.error(LoggerTexts.PRESENTATION_CTRL.APP_PUSH_STATE_NOT_UPDATED);
				}
			}
		}
	}

	public updateNextPushDates(updateAllPushDates = false) {
		const userState = this._getUserState();
		if (userState && this.pushConfig) {
			const pushType = this.getPushType();
			if (pushType === Enums.PUSH_SRC_TYPE.timed || updateAllPushDates) {
				this._updateTimedPushDate(userState, this.pushConfig);
			}
			if (pushType === Enums.PUSH_SRC_TYPE.appPush || updateAllPushDates) {
				this._updateAppPushDate(userState, this.pushConfig);
			}
			if (pushType === Enums.PUSH_SRC_TYPE.instantPush) {
				this._updateInstantPushDate(userState, this.pushConfig);
			}
			this._saveUserState(userState);
		}
	}

	public setPushContextData(pushContextData: PushContextData) {
		pushContextData.type = this.getPushType();
		this._contextDataController.setPushContext(pushContextData);
	}

	public setPushTelemetryContextData() {
		const userState = this._getUserState();
		const telemetryData = SurveyFactory.createTelemetryData();
		if (userState) {
			telemetryData.pushEventSnoozedCount = userState.snoozeCount;
			telemetryData.timeSinceLastPushEvent = Date.now() - userState.lastPush;
		}
		if (this.stateConfig?.state) {
			telemetryData.pushEventTriggeredCount = this.stateConfig.state.triggeredCount;
			telemetryData.pushEventExecutedCount = this.stateConfig.state.executedCount;
		}
		this._contextDataController.setPushTelemetryContext(telemetryData);
	}

	private _getUserState() {
		return Util.readUserState(this._storage);
	}

	private _saveUserState(userState: UserState) {
		this._storage.saveUserState(userState);
	}

	private _skipOnSnooze() {
		this.updateNextPushDates(true);
		const userState = this._getUserState();
		if (userState) {
			userState.resetSnoozeCount();
			this._saveUserState(userState);
		}
	}

	private _updateTimedPushDate(userState: UserState, pushConfig: PushConfig) {
		const quietPeriodOver = Date.now() + DateTimeUtil.getMillisecondsFromHours(pushConfig.quietPeriodInHrs);
		userState.timedPushDate = DateCalculator.calculateNextTimedPushDate(pushConfig);
		const appPushDate = userState.appPushDate;
		if (appPushDate && appPushDate < quietPeriodOver) {
			userState.appPushDate = quietPeriodOver;
		}
	}

	private _updateAppPushDate(userState: UserState, pushConfig: PushConfig) {
		const quietPeriodOver = Date.now() + DateTimeUtil.getMillisecondsFromHours(pushConfig.quietPeriodInHrs);
		userState.appPushDate = DateCalculator.calculateNextAppPushDate(pushConfig);
		const timedPushDate = userState.timedPushDate;
		if (timedPushDate && timedPushDate < quietPeriodOver) {
			userState.timedPushDate = quietPeriodOver;
		}
	}

	private _updateInstantPushDate(userState: UserState, pushConfig: PushConfig) {
		if (!this.stateConfig) {
			this._logger.error(LoggerTexts.PUSH_HANDLER.INSTANT_PUSH_STATE_NEXT_DATE_NOT_UPDATED);
			return;
		}
		const instantPushConfig = pushConfig.instantPushConfigs.get(this.stateConfig.combinedKey);
		if (instantPushConfig) {
			const nextPushDateTime = DateCalculator.calculateNextInstantPushDate(instantPushConfig);
			// 1. Update the next push date time for the instant push
			userState.updateInstantPushNextPushDateTime(this.stateConfig.state.areaId, this.stateConfig.state.triggerName, nextPushDateTime);
			// 2. Update appPushDate & timedPushDate if they are within the quiet period
			const quietPeriodOver = Date.now() + DateTimeUtil.getMillisecondsFromHours(pushConfig.quietPeriodInHrs);
			const appPushDate = userState.appPushDate;
			const timedPushDate = userState.timedPushDate;
			if (appPushDate && appPushDate < quietPeriodOver) {
				userState.appPushDate = quietPeriodOver;
			}
			if (timedPushDate && timedPushDate < quietPeriodOver) {
				userState.timedPushDate = quietPeriodOver;
			}
		}
	}

	private _updateSnoozeValues(currentSnoozeCount: number, pushConfig: PushConfig) {
		const userState = this._getUserState();
		if (userState) {
			const appPushDate = userState.appPushDate;
			const timedPushDate = userState.timedPushDate;
			const snoozeTimeOver = Date.now() + DateTimeUtil.getMillisecondsFromHours(pushConfig.snoozePeriodInHrs * currentSnoozeCount);
			if (appPushDate && appPushDate < snoozeTimeOver) {
				userState.appPushDate = snoozeTimeOver;
			}
			if (timedPushDate && timedPushDate < snoozeTimeOver) {
				userState.timedPushDate = snoozeTimeOver;
			}
			// Update the next push date time for the instant push with snooze time over
			if (this.getPushType() === Enums.PUSH_SRC_TYPE.instantPush) {
				if (!this.stateConfig) {
					this._logger.error(LoggerTexts.PUSH_HANDLER.INSTANT_PUSH_STATE_SNOOZE_DATE_NOT_UPDATED);
				} else {
					userState.updateInstantPushNextPushDateTime(this.stateConfig.state.areaId, this.stateConfig.state.triggerName, snoozeTimeOver);
				}
			}
			// Increase the snooze count for other push types except instant push
			if (this.getPushType() !== Enums.PUSH_SRC_TYPE.instantPush) {
				userState.increaseSnoozeCount();
			}
			this._saveUserState(userState);
		}
	}
}
